import React, { useEffect, useState } from 'react';
import Card from '../../components/common/Card';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import { useAuth } from '../../hooks/useAuth';
import { getSlaHours, saveSlaHours } from '../../utils/slaConfig';
import { validateEmail, validateRequired } from '../../utils/validators';
import configService from '../../services/configService';
import api from '../../services/api';
import EscalationMatrixManager from '../../components/features/escalation-matrix/EscalationMatrixManager';
import EmailTemplatesView from './EmailTemplatesView';
import SystemTypesConfig from './SystemTypeConfig';
import BranchAddressManager from '../../components/features/branch-address/BranchAddressManager';

const SettingsView = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [slaHours, setSlaHours] = useState(() => getSlaHours());
  const [smtpConfig, setSmtpConfig] = useState({
    smtpServer: '',
    port: '587',
    email: '',
    password: ''
  });
  const [storedSmtpPassword, setStoredSmtpPassword] = useState('');
  const [smtpErrors, setSmtpErrors] = useState({});

  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    contact: user?.contact || '',
    designation: user?.designation || '',
  });

  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isSavingSmtp, setIsSavingSmtp] = useState(false);

  useEffect(() => {
    const loadSmtpConfig = async () => {
      const result = await configService.getSmtpConfig();
      const nextSmtpConfig = result?.data || {
        smtpServer: '',
        port: '587',
        email: '',
        password: ''
      };

      setStoredSmtpPassword(nextSmtpConfig.password || '');
      setSmtpConfig({ ...nextSmtpConfig, password: '' });
    };

    loadSmtpConfig();
  }, []);

  const handleSlaChange = (value) => {
    setSlaHours(value);
  };

  const handleSaveSlaSettings = () => {
    saveSlaHours(slaHours);
    alert('SLA settings saved locally successfully.');
  };

  const handleSmtpFieldChange = (field, value) => {
    setSmtpConfig((prev) => ({ ...prev, [field]: value }));
    setSmtpErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateSmtpConfig = () => {
    const errors = {};
    const portValue = Number(smtpConfig.port);

    if (!validateRequired(smtpConfig.smtpServer)) {
      errors.smtpServer = 'SMTP server is required';
    }

    if (!validateRequired(smtpConfig.port)) {
      errors.port = 'Port is required';
    } else if (!Number.isInteger(portValue) || portValue <= 0 || portValue > 65535) {
      errors.port = 'Port must be between 1 and 65535';
    }

    if (!validateRequired(smtpConfig.email)) {
      errors.email = 'Email is required';
    } else if (!validateEmail(smtpConfig.email)) {
      errors.email = 'Please enter a valid email';
    }

    if (!validateRequired(smtpConfig.password) && !validateRequired(storedSmtpPassword)) {
      errors.password = 'Password is required';
    }

    setSmtpErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveSmtpSettings = async () => {
    if (!validateSmtpConfig()) {
      return;
    }

    setIsSavingSmtp(true);
    try {
      const passwordToSave = smtpConfig.password || storedSmtpPassword;

      await configService.saveSmtpConfig({
        smtpServer: smtpConfig.smtpServer,
        port: smtpConfig.port,
        email: smtpConfig.email,
        password: passwordToSave
      });
      setStoredSmtpPassword(passwordToSave);
      setSmtpConfig((prev) => ({ ...prev, password: '' }));
      alert('SMTP settings saved locally successfully.');
    } finally {
      setIsSavingSmtp(false);
    }
  };

  const handleResetSmtpSettings = async () => {
    const result = await configService.getSmtpConfig();
    const nextSmtpConfig = result?.data || {
      smtpServer: '',
      port: '587',
      email: '',
      password: ''
    };
    setStoredSmtpPassword(nextSmtpConfig.password || '');
    setSmtpConfig({ ...nextSmtpConfig, password: '' });
    setSmtpErrors({});
  };

  const handleProfileChange = (e, field) => {
    setProfile(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handlePasswordChange = (e, field) => {
    setPasswords(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    try {
      const response = await api.put(`/api/v1/users/${user.id}`, {
        name: profile.name,
        email: profile.email,
        contact: profile.contact,
        designation: profile.designation
      });
      updateUser(response.data);
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert(error.response?.data?.detail || 'Failed to update profile.');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!passwords.new_password || !passwords.current_password) {
      return alert('New and Current passwords are required.');
    }
    if (passwords.new_password !== passwords.confirm_password) {
      return alert('New passwords do not match.');
    }

    setIsUpdatingPassword(true);
    try {
      await api.put(`/api/v1/users/${user.id}/password`, {
        current_password: passwords.current_password,
        new_password: passwords.new_password
      });
      alert('Password updated successfully!');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      console.error('Failed to update password:', error);
      alert(error.response?.data?.detail || 'Failed to update password. Make sure your current password is correct.');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const tabsStyle = {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
    borderBottom: '1px solid var(--border)',
    paddingBottom: '0'
  };

  const tabStyle = (isActive) => ({
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '600',
    background: 'transparent',
    border: 'none',
    borderBottom: isActive ? '3px solid var(--primary)' : '3px solid transparent',
    color: isActive ? 'var(--primary)' : 'var(--text-secondary)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit'
  });

  const sectionStyle = {
    marginBottom: '32px'
  };

  const sectionTitleStyle = {
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    marginBottom: '16px'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '16px'
  };

  const renderProfileTab = () => (
    <div>
      <div style={sectionStyle}>
        <Card title="Personal Information" padding="24px">
          <div style={gridStyle}>
            <Input
              label="Full Name"
              value={profile.name}
              onChange={(e) => handleProfileChange(e, 'name')}
            />
            <Input
              label="Email"
              type="email"
              value={profile.email}
              onChange={(e) => handleProfileChange(e, 'email')}
            />
            <Input
              label="Phone"
              value={profile.contact}
              onChange={(e) => handleProfileChange(e, 'contact')}
            />
            <Input
              label="Designation"
              value={profile.designation || ''}
              onChange={(e) => handleProfileChange(e, 'designation')}
            />
            <Input
              label="Role"
              value={user?.role || ''}
              disabled
            />
          </div>
          <div style={{ marginTop: '20px', display: 'flex', gap: '12px' }}>
            <Button variant="primary" onClick={handleSaveProfile} disabled={isSavingProfile}>
              {isSavingProfile ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button 
               variant="secondary" 
               onClick={() => setProfile({ name: user?.name, email: user?.email, contact: user?.contact, designation: user?.designation })}
            >
              Cancel
            </Button>
          </div>
        </Card>
      </div>

      <div style={sectionStyle}>
        <Card title="Change Password" padding="24px">
          <div style={gridStyle}>
            <Input
              label="Current Password"
              type="password"
              canPeekPassword
              placeholder="Enter current password"
              value={passwords.current_password}
              onChange={(e) => handlePasswordChange(e, 'current_password')}
            />
            <Input
              label="New Password"
              type="password"
              canPeekPassword
              placeholder="Enter new password"
              value={passwords.new_password}
              onChange={(e) => handlePasswordChange(e, 'new_password')}
            />
            <Input
              label="Confirm Password"
              type="password"
              canPeekPassword
              placeholder="Confirm new password"
              value={passwords.confirm_password}
              onChange={(e) => handlePasswordChange(e, 'confirm_password')}
            />
          </div>
          <div style={{ marginTop: '20px' }}>
            <Button variant="primary" onClick={handleUpdatePassword} disabled={isUpdatingPassword}>
              {isUpdatingPassword ? 'Updating...' : 'Update Password'}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );

  const renderSystemTab = () => (
    <div>
      <div style={sectionStyle}>
        <Card title="SLA Configuration" padding="24px">
          <div style={gridStyle}>
            <Input
              label="SLA Response Time (hours)"
              type="number"
              value={slaHours}
              onChange={(e) => handleSlaChange(e.target.value)}
            />
          </div>
          <div style={{ marginTop: '20px' }}>
            <Button variant="primary" onClick={handleSaveSlaSettings}>Update SLA Settings</Button>
          </div>
        </Card>
      </div>

      <div style={sectionStyle}>
        <Card title="SMTP Configuration" padding="24px">
          <div style={gridStyle}>
            <Input
              label="SMTP Server"
              placeholder="smtp.example.com"
              value={smtpConfig.smtpServer}
              onChange={(e) => handleSmtpFieldChange('smtpServer', e.target.value)}
              error={smtpErrors.smtpServer}
            />
            <Input
              label="Port"
              type="number"
              placeholder="587"
              value={smtpConfig.port}
              onChange={(e) => handleSmtpFieldChange('port', e.target.value)}
              error={smtpErrors.port}
            />
            <Input
              label="Email"
              type="email"
              placeholder="noreply@example.com"
              value={smtpConfig.email}
              onChange={(e) => handleSmtpFieldChange('email', e.target.value)}
              error={smtpErrors.email}
            />
            <Input
              label="Password"
              type="password"
              placeholder="Enter SMTP password"
              value={smtpConfig.password}
              onChange={(e) => handleSmtpFieldChange('password', e.target.value)}
              error={smtpErrors.password}
              name="smtp-password"
              autoComplete="new-password"
              canPeekPassword
            />
          </div>
          <div style={{ marginTop: '20px', display: 'flex', gap: '12px' }}>
            <Button variant="primary" onClick={handleSaveSmtpSettings} disabled={isSavingSmtp}>
              {isSavingSmtp ? 'Saving...' : 'Save SMTP Settings'}
            </Button>
            <Button variant="secondary" onClick={handleResetSmtpSettings}>
              Reset
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );

  const renderNotificationsTab = () => (
    <div>
      <Card title="Notification Preferences" padding="24px">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {[
            { id: 'ticket_assigned', label: 'New ticket assigned', checked: true },
            { id: 'ticket_closed', label: 'Ticket closed', checked: true },
            { id: 'sla_breach', label: 'SLA breach warning', checked: true },
            { id: 'contract_expiry', label: 'Contract expiring soon', checked: true },
            { id: 'engineer_updates', label: 'Engineer status updates', checked: false },
            { id: 'daily_summary', label: 'Daily summary report', checked: true }
          ].map((option) => (
            <label
              key={option.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                background: 'var(--surface-light)',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              <input
                type="checkbox"
                defaultChecked={option.checked}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: '500' }}>
                {option.label}
              </span>
            </label>
          ))}
        </div>
        <div style={{ marginTop: '20px' }}>
          <Button variant="primary">Save Preferences</Button>
        </div>
      </Card>
    </div>
  );

  return (
    <div>
      <div style={tabsStyle}>
        <button
          style={tabStyle(activeTab === 'profile')}
          onClick={() => setActiveTab('profile')}
        >
           Profile
        </button>
        <button
          style={tabStyle(activeTab === 'system')}
          onClick={() => setActiveTab('system')}
        >
           System
        </button>
        <button
          style={tabStyle(activeTab === 'templates')}
          onClick={() => setActiveTab('templates')}
        >
           Email Templates
        </button>
        <button
          style={tabStyle(activeTab === 'escalation')}
          onClick={() => setActiveTab('escalation')}
        >
           Escalation Matrix
        </button>
        <button
          style={tabStyle(activeTab === 'systemtypes')}
          onClick={() => setActiveTab('systemtypes')}
        >
           System Types
        </button>
        <button
          style={tabStyle(activeTab === 'branchaddress')}
          onClick={() => setActiveTab('branchaddress')}
        >
           Branch Address
        </button>
      </div>

      {activeTab === 'profile' && renderProfileTab()}
      {activeTab === 'system' && renderSystemTab()}
      {activeTab === 'templates' && <EmailTemplatesView />}
      {activeTab === 'escalation' && <EscalationMatrixManager />}
      {activeTab === 'systemtypes' && <SystemTypesConfig />}
      {activeTab === 'branchaddress' && <BranchAddressManager/>}
      {activeTab === 'notifications' && renderNotificationsTab()}
    </div>
  );
};

export default SettingsView;